import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAccount, useChainId, usePublicClient, useSwitchChain, useWriteContract } from "wagmi";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { isAddress, type Address, type Hex } from "viem";
import { ERC721BadgeAbi } from "@atelier/abis";
import { byShortName } from "@/lib/chains";
import { api, ApiError } from "@/lib/api";
import { ArtworkImage } from "@/components/ArtworkImage";
import { ExplorerLink } from "@/components/ExplorerLink";

type BadgeInfo = {
  collection: {
    id: string;
    chainId: number;
    contractAddress: Address;
    name: string;
    symbol: string;
    description?: string;
    imageCid?: string;
    soulbound?: number;
    subsidized?: number;
  };
  signerAddress: Address;
  subsidized: boolean;
  relayerBalanceWei: string | null;
};

type SelfRedeem = { mode: "self"; signature: Hex; claimId: Hex; deadline: number };
type SubsidizedRedeem = { mode: "subsidized"; txHash: Hex };
type RedeemResp = SelfRedeem | SubsidizedRedeem;

export function Claim() {
  const params = useParams<{ shortName: string; address: string; code: string }>();
  const shortName = params.shortName ?? "";
  const address = (params.address ?? "").toLowerCase();
  const code = params.code ?? "";
  const chain = byShortName(shortName);
  const valid = !!chain && isAddress(address) && code.length > 0;

  const { address: wallet, isConnected } = useAccount();
  const walletChainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { writeContractAsync, isPending } = useWriteContract();
  const pc = usePublicClient({ chainId: chain?.chainId });

  const [status, setStatus] = useState<string | null>(null);
  const [resultTx, setResultTx] = useState<Hex | null>(null);

  const badgeQuery = useQuery({
    queryKey: ["badge-info", chain?.chainId, address],
    queryFn: () => api.get<BadgeInfo>(`/api/badges/${chain!.chainId}/${address}`),
    enabled: !!valid,
  });

  if (!valid) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="mb-2 text-xl font-semibold">Invalid claim link</h1>
        <p className="text-sm text-muted-foreground">The URL is missing chain, contract or code.</p>
      </div>
    );
  }

  const badge = badgeQuery.data?.collection;
  const subsidized = badgeQuery.data?.subsidized ?? false;
  const ipfsGateway = chain?.ipfsGateway ?? "https://dweb.link/ipfs/";

  const claim = async () => {
    if (!isConnected || !wallet || !chain) return;
    if (walletChainId !== chain.chainId) {
      switchChain({ chainId: chain.chainId });
      return;
    }
    setStatus("Validating code…");
    setResultTx(null);
    try {
      const resp = await api.post<RedeemResp>(`/api/badges/${chain.chainId}/${address}/redeem`, {
        code,
        to: wallet,
      });
      if (resp.mode === "subsidized") {
        setStatus("Confirming on-chain…");
        if (pc) await pc.waitForTransactionReceipt({ hash: resp.txHash });
        setResultTx(resp.txHash);
        setStatus("Badge claimed!");
        return;
      }
      // self-paid mode
      setStatus("Sign the mint in your wallet…");
      const txHash = await writeContractAsync({
        address: address as Address,
        abi: ERC721BadgeAbi,
        functionName: "mintWithSignature",
        args: [wallet as Address, resp.claimId, BigInt(resp.deadline), resp.signature],
        chainId: chain.chainId,
      });
      setStatus("Confirming on-chain…");
      if (pc) await pc.waitForTransactionReceipt({ hash: txHash });
      setResultTx(txHash);
      setStatus("Badge claimed!");
      // Tell the worker so the dashboard reflects the redemption
      void api.post(`/api/badges/${chain.chainId}/${address}/confirm`, {
        code,
        to: wallet,
        txHash,
      }).catch(() => undefined);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = (err.body as { error?: string; message?: string } | null);
        setStatus(`Error (${err.status}): ${detail?.message ?? detail?.error ?? "unknown"}`);
      } else {
        setStatus(`Error: ${(err as Error).message}`);
      }
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-10">
      <div className="card space-y-4">
        <div className="flex items-start gap-4">
          {badge?.imageCid && (
            <ArtworkImage
              alt={badge.name}
              src={`${ipfsGateway}${badge.imageCid}`}
              className="h-24 w-24 flex-shrink-0 rounded-lg border border-border object-cover"
            />
          )}
          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold">{badge?.name ?? "Badge"}</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              {chain?.name} ·{" "}
              {chain && <ExplorerLink chain={chain} kind="address" value={address} />}
            </p>
            {badge?.description && (
              <p className="mt-2 text-sm text-muted-foreground">{badge.description}</p>
            )}
          </div>
        </div>

        <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {subsidized
            ? "Gas is subsidized — the badge will be sent to your wallet for free."
            : "You'll sign one transaction in your wallet to claim this badge."}
          {badge?.soulbound ? " · Soulbound (non-transferable once claimed)." : null}
        </div>

        {!isConnected ? (
          <ConnectButton />
        ) : walletChainId !== chain!.chainId ? (
          <button className="btn-primary w-full" onClick={() => switchChain({ chainId: chain!.chainId })}>
            Switch to {chain!.name}
          </button>
        ) : (
          <button
            className="btn-primary w-full"
            disabled={isPending || badgeQuery.isLoading}
            onClick={() => void claim()}
          >
            {isPending ? "Claiming…" : "Claim badge"}
          </button>
        )}

        {status && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{status}</span>
            {resultTx && chain && (
              <ExplorerLink chain={chain} kind="tx" value={resultTx} label="view tx" className="text-xs" />
            )}
          </div>
        )}

        {wallet && (
          <div className="text-xs text-muted-foreground">
            Recipient: {chain && <ExplorerLink chain={chain} kind="address" value={wallet} className="text-xs" />}
          </div>
        )}
      </div>
    </div>
  );
}
